/**
 * TaxonomyQuickCreateRow (Slice 3.3) — the flagship inline quick-create surface.
 *
 * Per OQ-2 this ships as the CONTENT of an anchored AntD Popover mounted on the
 * existing `+` affordance of a parent row in `TaxonomyTreeEditor` (NOT a synthetic
 * inline `<Tree>` row), so the tree's drag/drop, search-filter and sort internals
 * are untouched. The filename is kept from the plan for traceability.
 *
 * Contract:
 * - 3 fields only, name-first, zh primary: 名稱（中文）(autofocus) → 名稱（英文）→ 代碼.
 *   NetSuite ref / comment / description / sort-order / status are NOT in create
 *   (deferred to the Wave-5 detail pane).
 * - Level is read-only ("第 n 層 / Level n", n = parent.depth + 1) plus a
 *   middle-truncated "Under: <path>" (leaf always kept) with a full-path tooltip.
 * - Code auto-suggests on name blur (Shopify suggest-and-confirm) via
 *   `suggestCategoryCode`; editable; "suggested" until the user edits it.
 * - Validate on blur (RHF `onTouched` + `reValidateMode: 'onChange'`); on code blur
 *   run `isCategoryCodeAvailable` → green tick / red "already used". Server
 *   `CODE_TAKEN` (409) via `useInlineCreateNode` is the race backstop (keeps the
 *   input + refocuses code).
 * - Save files + selects the new node; Save & add another files, keeps the parent
 *   fixed, resets the 3 fields, refocuses 名稱（中文）and does NOT close.
 * - State matrix: idle · validating · code-available · code-taken · save-in-flight
 *   (submit disabled, no double-file) · success · non-CODE_TAKEN server failure
 *   (localized inline error + aria-live announcement, values kept, retry allowed).
 * - Elevation (first consumer, Wave 2): the popover container carries
 *   `useElevation().popover` (applied by the host); inner fields round on
 *   `radiusTokens.card`, the level chip on `radiusTokens.chip`.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Input, Tag, Tooltip, Typography, theme } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import {
  quickCreateNodeSchema,
  type QuickCreateNodeFormData,
} from '@/schemas/forms/quickCreateNodeSchema';
import { buildBreadcrumb, useInlineCreateNode } from '@/hooks/useTaxonomy';
import { isCategoryCodeAvailable, suggestCategoryCode } from '@/utils/categoryUtils';
import { useFormStyles } from '@/styles/formStyles';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import type { ProductCategory } from '@/types/api/productCategory';

const { Text } = Typography;

/** Sync client-side verdict of the on-blur code check (server 409 is the backstop). */
type CodeStatus = 'idle' | 'available' | 'taken';

export interface TaxonomyQuickCreateRowProps {
  /** The parent row the popover is anchored to (never null — root create keeps the modal). */
  parent: ProductCategory;
  /** Full node set (active + inactive) — the basis for code suggestion + availability. */
  nodes: ProductCategory[];
  /**
   * A node was filed. `keepOpen` distinguishes the two save modes:
   * - `false` (Save): the host selects the new node and closes the popover.
   * - `true` (Save & add another): the host refreshes the tree but keeps the
   *   popover open on the same parent for a sibling loop.
   */
  onCreated: (newNodeId: string, keepOpen: boolean) => void;
  /** Dismiss without filing (Cancel / Esc). */
  onCancel: () => void;
}

const defaultValues: QuickCreateNodeFormData = { nameZh: '', nameEn: '', code: '' };

const TaxonomyQuickCreateRow = ({
  parent,
  nodes,
  onCreated,
  onCancel,
}: TaxonomyQuickCreateRowProps) => {
  const { t, i18n } = useTranslation(['taxonomy', 'common']);
  const { token } = theme.useToken();
  const formStyles = useFormStyles();
  const isZh = i18n.language.startsWith('zh');

  const [codeStatus, setCodeStatus] = useState<CodeStatus>('idle');
  const [codeEdited, setCodeEdited] = useState(false);
  const [saving, setSaving] = useState(false);
  const [serverFailed, setServerFailed] = useState(false);
  const [liveMessage, setLiveMessage] = useState('');

  const {
    control,
    handleSubmit,
    reset,
    setError,
    setFocus,
    setValue,
    getValues,
    clearErrors,
    formState: { errors, touchedFields },
  } = useForm<QuickCreateNodeFormData>({
    resolver: zodResolver(quickCreateNodeSchema),
    defaultValues,
    mode: 'onTouched',
    reValidateMode: 'onChange',
  });

  // Shared create + CODE_TAKEN guard path (S3.2) — the same submit path the root
  // CreateNodeModal uses, so the dup-guard UX (setError + setFocus on code) is
  // preserved verbatim. This component owns success / non-CODE_TAKEN messaging.
  const inlineCreate = useInlineCreateNode<QuickCreateNodeFormData>({ setError, setFocus });

  // Level + breadcrumb are read-only context; the popover only mounts on a real
  // parent row, so `parent.depth` is always defined.
  const childLevel = parent.depth + 1;
  const pathSegments = useMemo(
    () => buildBreadcrumb(nodes, parent.id, isZh ? 'zh' : 'en').split(' › ').filter(Boolean),
    [nodes, parent.id, isZh]
  );
  const fullPath = pathSegments.join(' › ');
  // The leaf (deepest segment = the parent itself) is always kept visible; only the
  // ancestor prefix ellipsizes, so a deep breadcrumb never blows out the row.
  const leafSegment = pathSegments[pathSegments.length - 1] ?? '';
  const prefixSegments =
    pathSegments.length > 1 ? `${pathSegments.slice(0, -1).join(' › ')} › ` : '';
  // Interpolating an empty parent yields just the localized label ("Under:" / 所屬分類：).
  const underLabel = t('quickCreate.belongsUnder', { parent: '' }).trim();

  // Autofocus 中文 on open (the primary field for the TW team). AntD Input mounts
  // inside the popover portal, so a rAF makes the focus land reliably.
  useEffect(() => {
    const raf = requestAnimationFrame(() => setFocus('nameZh'));
    return () => cancelAnimationFrame(raf);
  }, [setFocus]);

  const codeValue = useWatch({ control, name: 'code' });

  const inputStyle = { ...formStyles.inputStyle, borderRadius: radiusTokens.card };
  const requiredMark = (
    <span style={{ color: token.colorError, marginRight: token.marginXXS }}>*</span>
  );

  // Suggest-and-confirm: on a name blur, if the user has not edited the code, seed
  // it from the (unique) suggestion. Gated on a non-empty EN name so we never write
  // a bare "<PARENT>-" placeholder; the meaningful code lands on the EN-name blur.
  const maybeSuggestCode = useCallback(() => {
    if (codeEdited) return;
    const nameEn = getValues('nameEn').trim();
    if (!nameEn) return;
    setValue('code', suggestCategoryCode(parent, nameEn, nodes), { shouldValidate: false });
    setCodeStatus('idle');
  }, [codeEdited, getValues, nodes, parent, setValue]);

  const handleCodeUserEdit = useCallback(() => {
    if (!codeEdited) setCodeEdited(true);
    setCodeStatus('idle');
    setServerFailed(false);
    inlineCreate.reset();
    clearErrors('code'); // drop a stale server CODE_TAKEN; reValidate re-checks required
  }, [clearErrors, codeEdited, inlineCreate]);

  const handleCodeBlur = useCallback(() => {
    const value = getValues('code').trim();
    if (!value) {
      setCodeStatus('idle');
      return;
    }
    setCodeStatus(isCategoryCodeAvailable(value, nodes) ? 'available' : 'taken');
  }, [getValues, nodes]);

  const submitCore = async (data: QuickCreateNodeFormData, keepOpen: boolean) => {
    if (saving) return; // no double-file while a save is in flight
    setSaving(true);
    setServerFailed(false);
    const result = await inlineCreate.submit({
      code: data.code.trim(),
      nameEn: data.nameEn.trim(),
      nameZh: data.nameZh.trim(),
      parentId: parent.id,
    });
    setSaving(false);

    if (result.ok) {
      setLiveMessage(t('quickCreate.added', { name: data.nameZh.trim() }));
      if (keepOpen) {
        // Sibling loop: keep the parent fixed, clear the 3 fields, refocus 中文.
        reset(defaultValues);
        setCodeEdited(false);
        setCodeStatus('idle');
        inlineCreate.reset();
        onCreated(result.id, true);
        requestAnimationFrame(() => setFocus('nameZh'));
      } else {
        onCreated(result.id, false);
      }
      return;
    }

    if (result.guard === 'CODE_TAKEN') {
      // The hook already marked + refocused the code field; reflect + announce.
      setCodeStatus('taken');
      setLiveMessage(t('taxonomy:errors.CODE_TAKEN'));
      return;
    }
    // Any other server failure: localized inline error + announcement, values kept.
    setServerFailed(true);
    setLiveMessage(t('common:messages.createFailed'));
  };

  const onSaveAndClose = handleSubmit((data) => submitCore(data, false));
  const onSaveAndAddAnother = handleSubmit((data) => submitCore(data, true));

  const codeHasError =
    Boolean(errors.code) && (touchedFields.code || errors.code?.type === 'server');
  const codeInputStatus = codeHasError || codeStatus === 'taken' ? 'error' : undefined;

  const fieldError = (name: keyof QuickCreateNodeFormData) =>
    errors[name] && (touchedFields[name] || errors[name]?.type === 'server') ? (
      <Text style={formStyles.errorStyle}>{t(errors[name]?.message ?? '')}</Text>
    ) : null;

  const buttonStyle = {
    height: `${controlHeightTokens.controlHeightXL}px`,
    borderRadius: radiusTokens.control,
    flex: '1 1 120px',
  };

  return (
    <form
      onSubmit={onSaveAndClose}
      onKeyDown={(event) => {
        if (event.key === 'Escape') {
          event.stopPropagation();
          onCancel();
        }
      }}
      style={{
        width: 'min(300px, calc(100vw - 48px))',
        padding: token.paddingMD,
        boxSizing: 'border-box',
      }}
    >
      {/* Read-only context: title + level chip + "Under: <path>" (leaf kept). */}
      <div style={{ marginBottom: token.marginSM }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: token.marginXXS,
            marginBottom: token.marginXXS,
          }}
        >
          <Text strong>{t('taxonomy:tree.addChild')}</Text>
          <Tag bordered={false} style={{ margin: 0, borderRadius: radiusTokens.chip }}>
            {t('quickCreate.levelBadge', { level: childLevel })}
          </Tag>
        </div>
        <Tooltip title={t('quickCreate.belongsUnder', { parent: fullPath })}>
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              minWidth: 0,
              maxWidth: '100%',
              columnGap: token.marginXXS,
            }}
          >
            <span
              style={{ flexShrink: 0, color: token.colorTextSecondary, fontSize: token.fontSizeSM }}
            >
              {underLabel}
            </span>
            {prefixSegments && (
              <span
                style={{
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: token.colorTextSecondary,
                  fontSize: token.fontSizeSM,
                }}
              >
                {prefixSegments}
              </span>
            )}
            <span
              style={{
                flexShrink: 0,
                whiteSpace: 'nowrap',
                color: token.colorText,
                fontSize: token.fontSizeSM,
                fontWeight: token.fontWeightStrong,
              }}
            >
              {leafSegment}
            </span>
          </div>
        </Tooltip>
      </div>

      {/* 1) 名稱（中文）— primary, autofocused */}
      <Text style={formStyles.labelStyle}>
        {requiredMark}
        {t('quickCreate.nameZh')}
      </Text>
      <Controller
        name="nameZh"
        control={control}
        render={({ field }) => (
          <Input
            {...field}
            autoFocus
            size="large"
            style={inputStyle}
            status={errors.nameZh ? 'error' : undefined}
            onBlur={() => {
              field.onBlur();
              maybeSuggestCode();
            }}
          />
        )}
      />
      {fieldError('nameZh')}

      {/* 2) 名稱（英文）*/}
      <Text style={{ ...formStyles.labelStyle, marginTop: token.marginSM }}>
        {requiredMark}
        {t('quickCreate.nameEn')}
      </Text>
      <Controller
        name="nameEn"
        control={control}
        render={({ field }) => (
          <Input
            {...field}
            size="large"
            style={inputStyle}
            status={errors.nameEn ? 'error' : undefined}
            onBlur={() => {
              field.onBlur();
              maybeSuggestCode();
            }}
          />
        )}
      />
      {fieldError('nameEn')}

      {/* 3) 代碼 — auto-suggested, editable, availability-checked on blur */}
      <Text style={{ ...formStyles.labelStyle, marginTop: token.marginSM }}>
        {requiredMark}
        {t('quickCreate.code')}
      </Text>
      <Controller
        name="code"
        control={control}
        render={({ field }) => (
          <Input
            {...field}
            size="large"
            placeholder={t('taxonomy:create.codePlaceholder')}
            style={inputStyle}
            status={codeInputStatus}
            onChange={(event) => {
              field.onChange(event);
              handleCodeUserEdit();
            }}
            onBlur={() => {
              field.onBlur();
              handleCodeBlur();
            }}
          />
        )}
      />
      {codeHasError ? (
        <Text style={formStyles.errorStyle}>{t(errors.code?.message ?? '')}</Text>
      ) : codeStatus === 'available' ? (
        <Text style={{ ...formStyles.errorStyle, color: token.colorSuccess }}>
          <CheckCircleFilled style={{ marginRight: token.marginXXS }} />
          {t('quickCreate.codeAvailable')}
        </Text>
      ) : codeStatus === 'taken' ? (
        <Text style={formStyles.errorStyle}>{t('quickCreate.codeTaken')}</Text>
      ) : codeValue && !codeEdited ? (
        <Text
          type="secondary"
          style={{ display: 'block', marginTop: token.marginXXS, fontSize: token.fontSizeSM }}
        >
          {t('quickCreate.codeSuggested')}
        </Text>
      ) : null}

      {serverFailed && (
        <Text style={{ ...formStyles.errorStyle, display: 'block', marginTop: token.marginXS }}>
          {t('common:messages.createFailed')}
        </Text>
      )}

      {/* Save (files + selects) · Save & add another (files, keeps parent, loops) */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: token.marginXS,
          marginTop: token.marginSM,
        }}
      >
        <Button type="primary" htmlType="submit" size="large" loading={saving} style={buttonStyle}>
          {t('quickCreate.save')}
        </Button>
        <Button
          htmlType="button"
          size="large"
          disabled={saving}
          onClick={onSaveAndAddAnother}
          style={buttonStyle}
        >
          {t('quickCreate.saveAndAddAnother')}
        </Button>
      </div>
      <Button
        type="text"
        htmlType="button"
        size="small"
        disabled={saving}
        onClick={onCancel}
        style={{ marginTop: token.marginXXS }}
      >
        {t('common:actions.cancel')}
      </Button>

      {/* Visually-hidden polite announcer: add-success, guard, and failure copy. */}
      <div aria-live="polite" className="tw-sr-only">
        {liveMessage}
      </div>
    </form>
  );
};

export default TaxonomyQuickCreateRow;
